import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Record } from 'immutable';
import moment from 'moment';
import { useLocation, matchPath } from 'react-router';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Types
import { MeetingItem, ItemType } from 'Shared/types/MeetingItem.type';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';

// Selectors
import * as meetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';

// Components
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpAlert from 'Wpds/components/alert/WpAlert';
import CopyMoveActionItemModal from 'Shared/components/modals/DuplicateModal/CopyMoveActionItemModal';
import RecurrenceModal from 'Shared/components/RecurrenceModal/RecurrenceModal';
import AddToGoalModal from 'Shared/components/modals/AddToGoalModal/AddToGoalModalV2';
import DeleteModal from 'Shared/components/ConfirmModal/ConfirmModal';
import CopyToAsanaModal from 'Shared/components/modals/AsanaModal/CopyToAsanaModal';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  meetingItem: Record<MeetingItem>;
  handleAttachFileClick?: Function;
  handleClosePanel?: Function;
};

const MeetingItemActionsDropdown = ({ meetingItem, handleAttachFileClick, handleClosePanel }: Props) => {
  const { hasAsanaIntegration } = useCurrentUser();
  const { isFreePlan } = useOrganizationSubscription();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { tr } = useTranslation();
  // Can't use PageContext because this component
  // is outside of the PageContext Provider
  const isGoalDetailPageContext = !!matchPath(pathname, { path: PageUrls.GOALS_DETAIL_PAGE });
  const isHomePageContext = !!matchPath(pathname, { path: PageUrls.HOME_PAGE_ITEM });

  const [isCopyMoveModalOpen, toggleIsCopyMoveModalOpen] = useToggle(false);
  const [isRecurrenceModalOpen, toggleIsRecurrenceModalOpen] = useToggle(false);
  const [isAddToGoalModalOpen, toggleAddToGoalModal] = useToggle(false);
  const [isCopyToAsanaModalOpen, toggleIsCopyToAsanaModal] = useToggle(false);
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [isDeleting, toggleIsDeleting] = useToggle(false);

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const isComplete = MeetingItemHelpers.getCompleted(meetingItem);
  const goalId = MeetingItemHelpers.getGoalId(meetingItem);
  const itemType = MeetingItemHelpers.getItemType(meetingItem);
  const asanaData = MeetingItemHelpers.getAsanaData(meetingItem);
  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);
  const dueBy = MeetingItemHelpers.getDueBy(meetingItem);
  const isSection = MeetingItemHelpers.getIsSection(meetingItem);

  const subItems = useSelector(state => meetingItemSelectors.getSubMeetingItems(state, { parentUuid: uuid }));

  const hasRecurrence = MeetingItemUtils.hasRecurrence(recurringWorkspaceItem);
  const hasSubItems = MeetingItemUtils.hasSubItems(subItems);
  const isActionItem = MeetingItemUtils.isActionItem(itemType);
  const isLinkedToGoal = MeetingItemUtils.isLinkedToGoal(goalId);
  const type = MeetingItemUtils.getTypeHumanReadable(itemType);
  const isLinkedToAsana = MeetingItemUtils.isLinkedToAsana(asanaData);

  // const hasJiraIntegration = useSelector(state => state.user.hasJiraIntegration);

  const handleDisconnectFromAsana = useCallback(() => {
    dispatch(meetingItemActions.disconnectAsanaTask(uuid));
  }, []);

  const handleDeleteItem = useCallback(() => {
    toggleIsDeleting();
    dispatch(meetingItemActions.deleteMeetingItem(meetingItem)).then(() => {
      handleClosePanel && handleClosePanel();
    });
  }, [toggleIsDeleting]);

  const handleConvertItemClick = useCallback(() => {
    const newType = isActionItem ? ItemType.discussionTopic : ItemType.actionItem;
    const convertedDueyBy =
      isActionItem || dueBy
        ? dueBy
        : moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(19, 'hours').unix();

    dispatch(
      meetingItemActions.convertDiscussionTopicAndActionItem({ meetingItemSlug: uuid, newType, dueBy: convertedDueyBy })
    );
  }, [dispatch, uuid, dueBy, itemType]);

  const showAttachFile = handleAttachFileClick && !isSection;
  const showCopyMoveButton = !isLinkedToGoal && !isComplete;
  const showAddToGoal = !isGoalDetailPageContext && isActionItem;
  const showConvertToButton = !isLinkedToGoal && !isHomePageContext && !isSection && !(isSection && !isActionItem);
  const showAsanaActions = hasAsanaIntegration && !isFreePlan && !isSection;
  // const showJiraActions = hasJiraIntegration && billingState !== billingStates.FREE_PLAN && !isSection;
  const recurrenceLabel = hasRecurrence
    ? isActionItem
      ? tr('perform.meeting_item.actions_dropdown.edit_recurring_item')
      : tr('perform.meeting_item.actions_dropdown.edit_recurring_topic')
    : isActionItem
    ? tr('perform.meeting_item.actions_dropdown.create_recurring_item')
    : tr('perform.meeting_item.actions_dropdown.create_recurring_topic');

  return (
    <>
      <WpDropdown>
        <WpDropdownToggle>
          <WpButton variant="tertiary" size="sm">
            {tr('perform.meeting_item.actions_dropdown.button_label')}
            <WpIcon name="angle-down" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {showAttachFile && (
            <WpDropdownMenuItem onClick={handleAttachFileClick}>
              <WpIcon name="paperclip" />
              {tr('perform.meeting_item.actions_dropdown.attach_file')}
            </WpDropdownMenuItem>
          )}
          {isActionItem && (
            <WpDropdownMenuItem onClick={toggleIsRecurrenceModalOpen}>
              <WpIcon name="recycle" />
              {recurrenceLabel}
            </WpDropdownMenuItem>
          )}
          {showCopyMoveButton && (
            <WpDropdownMenuItem onClick={toggleIsCopyMoveModalOpen}>
              <WpIcon name="copy" />
              {tr('perform.meeting_item.actions_dropdown.move_copy')}
            </WpDropdownMenuItem>
          )}
          {showAddToGoal && (
            <WpDropdownMenuItem onClick={toggleAddToGoalModal}>
              <WpIcon name="bullseye-arrow" />
              {isLinkedToGoal
                ? tr('perform.meeting_item.actions_dropdown.change_goal')
                : tr('perform.meeting_item.actions_dropdown.add_to_goal')}
            </WpDropdownMenuItem>
          )}
          {showConvertToButton && (
            <WpDropdownMenuItem onClick={handleConvertItemClick}>
              <WpIcon name="random" />
              {isActionItem
                ? tr('perform.meeting_item.actions_dropdown.convert_to_discussion_topic')
                : tr('perform.meeting_item.actions_dropdown.convert_to_action_item')}
            </WpDropdownMenuItem>
          )}
          <WpDropdownMenuDivider />
          {showAsanaActions && !isLinkedToAsana && (
            <WpDropdownMenuItem onClick={toggleIsCopyToAsanaModal}>
              <WpIcon prefix="fac" name="asana" />
              {tr('perform.meeting_item.actions_dropdown.copy_to_asana')}
            </WpDropdownMenuItem>
          )}
          {showAsanaActions && isLinkedToAsana && (
            <WpDropdownMenuItem onClick={handleDisconnectFromAsana}>
              <WpIcon prefix="fac" name="asana" />
              {tr('perform.meeting_item.actions_dropdown.disconnect_from_asana')}
            </WpDropdownMenuItem>
          )}
          {/*
          // TODO (josh): add jira in near future
          {showJiraActions && (
            <WpDropdownMenuItem>
              <WpIcon prefix="fab" name="jira" />
              Copy to Jira
            </WpDropdownMenuItem>
          )} */}
          {showAsanaActions && <WpDropdownMenuDivider />}
          <WpDropdownMenuItem color="danger" onClick={toggleIsDeleteModalOpen}>
            <WpIcon name="trash" />
            {tr('perform.meeting_item.actions_dropdown.delete_item')}
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
      <CopyMoveActionItemModal
        isOpen={isCopyMoveModalOpen}
        toggleOpen={toggleIsCopyMoveModalOpen}
        meetingItem={meetingItem}
        handleCloseDetailsPanel={handleClosePanel}
      />
      <RecurrenceModal
        isOpen={isRecurrenceModalOpen}
        toggleModal={toggleIsRecurrenceModalOpen}
        meetingItem={meetingItem}
      />
      <CopyToAsanaModal
        isOpen={isCopyToAsanaModalOpen}
        toggleModal={toggleIsCopyToAsanaModal}
        meetingItem={meetingItem}
      />
      <AddToGoalModal isOpen={isAddToGoalModalOpen} toggleModal={toggleAddToGoalModal} meetingItem={meetingItem} />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onToggle={toggleIsDeleteModalOpen}
        onConfirmClick={handleDeleteItem}
        isProcessing={isDeleting}
      >
        <p>{tr('perform.meeting_item.actions_dropdown.delete_modal_text', type)}</p>
        {hasSubItems && (
          <WpAlert type="danger">{tr('perform.meeting_item.actions_dropdown.delete_modal_subitems_alert')}</WpAlert>
        )}
      </DeleteModal>
    </>
  );
};

export default MeetingItemActionsDropdown;
